import { useEffect, useRef, useState } from "react";
import { DoorOpen, DoorClosed, Cpu, BatteryMedium, WifiOff, Siren, Check, Camera, X, LogIn, LogOut, ClipboardList, MapPin, Activity } from "lucide-react";
import { track } from "@/lib/track";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CACHE_KEY = "mws_marktplatz_cache";
const SCAN_KEY = "mws_scans_v1";
const PLATE_LETTERS = "ABCDEFGHJKLMNPRSTUVWXYZ";

function randomPlate() {
  const l = () => PLATE_LETTERS[Math.floor(Math.random() * PLATE_LETTERS.length)];
  return `WI-${l()}${l()} ${Math.floor(1000 + Math.random() * 8999)}`;
}
function loadCache() {
  try { const r = JSON.parse(localStorage.getItem(CACHE_KEY)); if (r && r.status) return r; } catch (e) {}
  return null;
}
function loadScans() {
  try { const r = JSON.parse(localStorage.getItem(SCAN_KEY)); if (r && r.events) return r; } catch (e) {}
  return { inside: [], events: [], _eid: 0 };
}
function timeStr(ms) { return new Date(ms).toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit", second: "2-digit" }); }
function dateStr(ms) { return new Date(ms).toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric" }); }
function fmtHours(ms) {
  const h = Math.floor(Math.max(0, ms) / 3600000);
  if (h < 1) { const m = Math.floor(Math.max(0, ms) / 60000); return `${m} Min`; }
  if (h < 48) return `${h} Std`;
  return `${Math.floor(h / 24)} Tagen`;
}
function clockHM(ms) { return new Date(ms).toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" }); }
function isSameDay(ms) { const d = new Date(ms), n = new Date(); return d.getDate() === n.getDate() && d.getMonth() === n.getMonth() && d.getFullYear() === n.getFullYear(); }
function timeLabel(ms) { return isSameDay(ms) ? timeStr(ms) : `${new Date(ms).toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit" })} ${clockHM(ms)}`; }
function fmtHM(ms) { const t = Math.max(0, Math.floor(ms / 1000)); const h = Math.floor(t / 3600), m = Math.floor((t % 3600) / 60); const p = (n) => String(n).padStart(2, "0"); return `${p(h)}:${p(m)}`; }
function fmtSeit(ms) {
  const t = Math.max(0, Math.floor(ms / 1000)), h = Math.floor(t / 3600), m = Math.floor((t % 3600) / 60), s = t % 60, p = (n) => String(n).padStart(2, "0");
  return h > 0 ? `seit: ${p(h)} Stunden ${p(m)} Minuten ${p(s)} Sekunden` : `seit: ${p(m)} Minuten ${p(s)} Sekunden`;
}
function fmtOpenDur(ms) {
  const t = Math.max(0, Math.floor(ms / 1000));
  if (t < 120) return `Offen: ${t} Sekunden`;
  const m = Math.floor(t / 60), s = t % 60;
  return `Offen: ${m} min ${s} s`;
}
function fmtKeepAlive(ms) { const m = Math.floor(Math.max(0, ms) / 60000); if (m < 1) return "gerade eben"; if (m < 60) return `vor ${m} Min`; const h = Math.floor(m / 60); return `vor ${h} Std ${m % 60} Min`; }

const SSG_BANNER = "https://customer-assets-gfyr7b9c.emergentagent.net/job_checkpoint-monitor/artifacts/p2bxbg7t_image.png";
const Brand = () => (
  <>
    <div className="mp-banner"><img src={SSG_BANNER} alt="TERRORSPERREN.DIGITAL" /></div>
    <div className="mp-logo-strip">
      <a href="/" className="mp-brand" data-testid="brand-home" aria-label="Zur Startseite">
        <span className="mp-brand-bar" />
        <div className="mp-brand-title">SPERREN<span className="dot">.</span>DIGITAL</div>
        <div className="mp-brand-sub">IoT für Zufahrtssperren</div>
      </a>
    </div>
  </>
);

export default function MarktplatzWidgetLive() {
  const [data, setData] = useState(loadCache);
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(!loadCache());
  const [now, setNow] = useState(Date.now());
  const [lastFetch, setLastFetch] = useState(null);
  const [scans, setScans] = useState(loadScans);
  const [scan, setScan] = useState(false);
  const [scanStep, setScanStep] = useState("camera");
  const [scannedPlate, setScannedPlate] = useState("");
  const [scannedReal, setScannedReal] = useState(false);
  const [showList, setShowList] = useState(false);
  const [camError, setCamError] = useState(false);
  const [detailed, setDetailed] = useState(false);
  const [ackedSosMs, setAckedSosMs] = useState(0);
  const aliveRef = useRef(true);
  const videoRef = useRef(null);
  const streamRef = useRef(null);

  useEffect(() => { localStorage.setItem(SCAN_KEY, JSON.stringify(scans)); }, [scans]);

  const load = async () => {
    try {
      const r = await fetch(`${API}/mws/marktplatz`);
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const j = await r.json();
      if (!aliveRef.current) return;
      setData(j); setErr(null); setLastFetch(Date.now());
      try { localStorage.setItem(CACHE_KEY, JSON.stringify(j)); } catch (e) {}
    } catch (e) { if (aliveRef.current) setErr("MWS nicht erreichbar"); }
    finally { if (aliveRef.current) setLoading(false); }
  };

  useEffect(() => {
    aliveRef.current = true;
    load();
    const t = setInterval(load, 10000);
    const c = setInterval(() => setNow(Date.now()), 1000);
    return () => { aliveRef.current = false; clearInterval(t); clearInterval(c); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Kamera nur im Schritt "camera"
  useEffect(() => {
    if (!scan || scanStep !== "camera") {
      if (streamRef.current) { streamRef.current.getTracks().forEach((t) => t.stop()); streamRef.current = null; }
      return;
    }
    setCamError(false);
    let cancelled = false;
    navigator.mediaDevices?.getUserMedia({ video: { facingMode: "environment" } })
      .then((stream) => { if (cancelled) { stream.getTracks().forEach((t) => t.stop()); return; } streamRef.current = stream; if (videoRef.current) videoRef.current.srcObject = stream; })
      .catch(() => setCamError(true));
    return () => { cancelled = true; };
  }, [scan, scanStep]);

  const pushScan = (s, type, text, extra) => { const id = (s._eid || 0) + 1; return { ...s, _eid: id, events: [{ id, type, tsMs: Date.now(), text, ...extra }, ...s.events].slice(0, 60) }; };
  const einfahrt = (plate, real) => { track("einfahrt", { mode: "live", real }); setScans((s) => pushScan({ ...s, inside: [...s.inside, { plate, inMs: Date.now() }] }, "in", `Einfahrt ${plate}`, { plate, in_ms: Date.now(), real })); };
  const ausfahrtByPlate = (plate, real) => { track("ausfahrt", { mode: "live", real }); setScans((s) => { const inside = [...s.inside]; const idx = inside.findIndex((v) => v.plate === plate); let in_ms = null; if (idx >= 0) { in_ms = inside[idx].inMs; inside.splice(idx, 1); } return pushScan({ ...s, inside }, "out", `Ausfahrt ${plate}`, { plate, in_ms, real }); }); };

  const openScan = () => { track("scan_open", { mode: "live" }); setScannedPlate(""); setScannedReal(false); setScanStep("camera"); setScan(true); };
  const closeScan = () => { setScan(false); setScanStep("camera"); };
  const confirmScan = async () => {
    let b64 = null; const v = videoRef.current;
    if (v && v.videoWidth) { const c = document.createElement("canvas"); c.width = v.videoWidth; c.height = v.videoHeight; c.getContext("2d").drawImage(v, 0, 0); b64 = c.toDataURL("image/jpeg", 0.8); }
    setScanStep("scanning");
    let plate = null, real = false;
    if (b64) { try { const r = await fetch(`${API}/scan-plate`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ image_base64: b64 }) }); const j = await r.json(); if (j.plate && !/UNBEKANNT/i.test(j.plate) && /\d/.test(j.plate)) { plate = j.plate.trim(); real = true; } } catch (e) {} }
    if (!plate) { plate = randomPlate(); real = false; }
    track("plate_recognized", { mode: "live", real });
    setScannedPlate(plate); setScannedReal(real); setScanStep("result");
  };

  if (loading && !data) {
    return (
      <div className="mp-widget mp-live geschlossen" data-testid="marktplatz-widget-live">
        <Brand />
        <div className="mp-status" style={{ padding: "3rem 1.2rem", justifyContent: "center" }}>
          <Camera className="mp-spin" size={26} /> <span style={{ marginLeft: 10 }}>Lade MWS-Daten…</span>
        </div>
      </div>
    );
  }

  const status = data?.status || "unbekannt";
  const offen = status === "offen";
  const unbekannt = status === "unbekannt";
  const elapsed = data ? now - data.lastChangeMs : 0;
  const fillPct = offen ? Math.min(100, (elapsed / 60000) * 100) : 0;
  const updatedTs = lastFetch || data?.fetchedAt;
  const batteryPct = data?.battery != null ? Math.round((data.battery / 255) * 100) : null;
  const title = data?.title || "Terrorsperre";

  const mwsEvents = (data?.events || []).map((e) => ({ ts_ms: e.ts_ms, type: e.type, text: e.text, open_ms: e.open_ms, paired: e.paired, lat: e.lat, lon: e.lon, acc: e.acc, src: "mws" }));
  const localEvents = scans.events.map((e) => ({ ts_ms: e.tsMs, type: e.type, text: e.text, in_ms: e.in_ms, real: e.real, src: "local" }));
  const allEvents = [...mwsEvents, ...localEvents]
    .filter((e) => detailed || !(e.type === "open" && e.paired))
    .sort((a, b) => b.ts_ms - a.ts_ms).slice(0, 60);
  const iconFor = (t) => t === "open" ? <DoorOpen size={15} /> : t === "close" ? <DoorClosed size={15} /> : t === "in" ? <LogIn size={15} /> : t === "out" ? <LogOut size={15} /> : t === "sos" ? <Siren size={15} /> : t === "gps" ? <MapPin size={15} /> : <Check size={15} />;

  if (data?.connectionLost) {
    return (
      <div className="mp-widget mp-live mp-lost" data-testid="marktplatz-widget-live">
        <Brand />
        <div className="mp-lost-body" data-testid="connection-lost">
          <WifiOff size={56} className="mp-lost-ico" />
          <div className="mp-lost-title">Verbindung zu Sensor verloren</div>
          <div className="mp-lost-sub">
            Letztes Signal:{" "}
            {data?.lastSeenMs ? `${dateStr(data.lastSeenMs)} ${timeStr(data.lastSeenMs)}` : "unbekannt"}
          </div>
        </div>
      </div>
    );
  }

  const showSos = data?.sos && (!ackedSosMs || (data?.sosMs || 0) > ackedSosMs);

  return (
    <div className={`mp-widget mp-live ${status}${showSos ? " sos" : ""}`} data-testid="marktplatz-widget-live">
      <Brand />

      <div className="mp-live-head">
        {offen && <div className="mp-fill" style={{ height: `${fillPct}%` }} data-testid="open-fill" />}
        <div className="mp-barrier tall" data-testid="barrier-icon" aria-label={offen ? "Sperre offen" : "Sperre geschlossen"}>
          <span className="base" /><span className="post" /><span className="beam" /><span className="arm" />
        </div>
        <div className="mp-live-main">
          <div className="mp-toprow">
            <div className="mp-loc" data-testid="sperre-title">{title}</div>
            <div className="mp-updated" data-testid="updated-info">
              <span className="cap">Aktualisiert</span>
              <span className="val">{updatedTs ? timeStr(updatedTs) : "—"}</span>
              <span className="mp-date">{dateStr(now)}</span>
            </div>
          </div>
          <div className="mp-wordline">
            <span className="word" data-testid="status-word">{unbekannt ? "Status unbekannt" : offen ? "OFFEN" : "GESCHLOSSEN"}</span>
            {offen && <span className="timer" data-testid="open-timer">{fmtSeit(elapsed)}</span>}
            {unbekannt && (
              <span className="mp-secured mp-nodata" data-testid="no-data-info">
                <WifiOff size={14} />
                keine Neigungsdaten{data?.lastStatusMs ? ` seit ${fmtHours(now - data.lastStatusMs)}` : ""}
              </span>
            )}
          </div>
        </div>
      </div>

      <div className="mp-body">
        {showSos && (
          <div className="mp-sos" data-testid="sos-banner">
            <Siren size={18} />
            <span className="mp-sos-txt">Unterstützungsanforderung!</span>
            <button
              className="mp-sos-ack"
              onClick={() => setAckedSosMs(data?.sosMs || Date.now())}
              data-testid="sos-ack-btn"
            >
              Quittieren
            </button>
          </div>
        )}

        <div className="mp-devrow" data-testid="device-info">
          <span className="mp-devchip"><Cpu size={14} /> {data?.dev_eui || "—"}</span>
          <span className="mp-devchip"><BatteryMedium size={14} /> {batteryPct != null ? `${batteryPct} %` : "—"}</span>
          {data?.keepAliveMs && (<span className="mp-keepalive" data-testid="keepalive-info"><Activity size={13} /> Keep alive {fmtKeepAlive(now - data.keepAliveMs)}</span>)}
        </div>

        <ul className="mp-log" data-testid="event-log">
          {allEvents.length === 0 && <li style={{ color: "#6c757d" }}>Keine Meldungen vorhanden</li>}
          {allEvents.map((ev, i) => {
            const cls = ev.type === "open" || ev.type === "close" ? ev.type : ev.type === "sos" ? "sos" : ev.type === "gps" ? "gps" : (ev.real ? "real" : "sim");
            if (ev.type === "gps" && ev.lat != null && ev.lon != null) {
              return (
                <li key={`${ev.src}-${ev.ts_ms}-${i}`}>
                  <span className="lt">{timeLabel(ev.ts_ms)}</span>
                  <span className={`li-ico ${cls}`}>{iconFor(ev.type)}</span>
                  <a
                    className="mp-gps-link"
                    href={`https://www.google.com/maps?q=${ev.lat},${ev.lon}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`gps-link-${i}`}
                  >
                    {ev.text}
                    {ev.acc != null && <span className="mp-opendur"> ({Math.round(ev.acc)}m)</span>}
                  </a>
                </li>
              );
            }
            return (
              <li key={`${ev.src}-${ev.ts_ms}-${i}`}>
                <span className="lt">{timeLabel(ev.ts_ms)}</span>
                <span className={`li-ico ${cls}`}>{iconFor(ev.type)}</span>
                <span>
                  {ev.text}
                  {ev.type === "close" && ev.open_ms != null && (<span className="mp-opendur"> ({fmtOpenDur(ev.open_ms)})</span>)}
                  {ev.type === "out" && ev.in_ms != null && (<span className="mp-opendur"> ({clockHM(ev.in_ms)} - {clockHM(ev.ts_ms)})</span>)}
                </span>
              </li>
            );
          })}
        </ul>

        <div className="mp-controls">
          <div className="mp-viewtoggle" data-testid="view-toggle">
            <button className={`mp-toggle-opt ${!detailed ? "active" : ""}`} onClick={() => setDetailed(false)} data-testid="toggle-kompakt">Kompakt</button>
            <button className={`mp-toggle-opt ${detailed ? "active" : ""}`} onClick={() => setDetailed(true)} data-testid="toggle-ausfuehrlich">Ausführlich</button>
          </div>
          {err && <span className="mp-fetchinfo" style={{ color: "#dc3545" }}>{err}</span>}
        </div>
      </div>

      {scan && (
        <div className="mp-scan-overlay" data-testid="scan-modal" onClick={closeScan}>
          <div className="mp-scan-box" onClick={(e) => e.stopPropagation()}>
            <div className="mp-scan-head">
              <span>{scanStep === "result" ? "Kennzeichen erkannt" : scanStep === "scanning" ? "Kennzeichenerkennung" : "Nummernschild Scannen"}</span>
              <button className="mp-scan-x" onClick={closeScan} data-testid="scan-close" aria-label="Schließen"><X size={18} /></button>
            </div>
            {scanStep === "camera" && (
              <>
                <div className="mp-scan-cam">
                  {!camError ? (<video ref={videoRef} autoPlay playsInline muted className="mp-scan-video" />) : (<div className="mp-scan-fallback"><Camera size={40} /><span>Kamera nicht verfügbar</span></div>)}
                  <div className="mp-scan-frame"><span className="mp-scan-line" /></div>
                </div>
                <button className="mp-btn primary mp-scan-confirm" onClick={confirmScan} data-testid="scan-confirm"><Camera size={15} /> Nummernschild erfassen</button>
              </>
            )}
            {scanStep === "scanning" && (<div className="mp-scan-progress" data-testid="scan-progress"><Camera size={40} className="mp-spin" /><span>Kennzeichenerkennung läuft…</span></div>)}
            {scanStep === "result" && (
              <div className="mp-scan-result" data-testid="scan-result">
                <span className={`al-plate mp-plate-big ${scannedReal ? "" : "sim"}`}>{scannedPlate}</span>
                <div className="mp-scan-srclabel">{scannedReal ? "Kamera erkannt" : "Simuliert"}</div>
                <div className="mp-scan-mode">
                  <button className="mp-btn primary" onClick={() => { einfahrt(scannedPlate, scannedReal); closeScan(); }} data-testid="result-einfahrt"><LogIn size={16} /> Einfahrt</button>
                  <button className="mp-btn toggle" onClick={() => { ausfahrtByPlate(scannedPlate, scannedReal); closeScan(); }} data-testid="result-ausfahrt"><LogOut size={16} /> Ausfahrt</button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {showList && (
        <div className="mp-scan-overlay" data-testid="list-modal" onClick={() => setShowList(false)}>
          <div className="mp-scan-box" onClick={(e) => e.stopPropagation()}>
            <div className="mp-scan-head"><span>Fahrzeugliste</span><button className="mp-scan-x" onClick={() => setShowList(false)} data-testid="list-close"><X size={18} /></button></div>
            <div className="mp-list">
              <div className="mp-list-cat">Im Gelände ({scans.inside.length})</div>
              {scans.inside.length === 0 && <div className="mp-list-empty">Keine Fahrzeuge im Gelände</div>}
              {[...scans.inside].reverse().map((v, i) => (<div className="mp-list-row inside" key={`in-${i}`}><span className="al-plate">{v.plate}</span><span className="mp-list-time">{fmtHM(now - v.inMs)} <small>im Gelände</small></span></div>))}
              <div className="mp-list-cat">Ausgefahren</div>
              {scans.events.filter((e) => e.type === "out").length === 0 && <div className="mp-list-empty">Noch keine Ausfahrten</div>}
              {scans.events.filter((e) => e.type === "out").map((e) => (<div className="mp-list-row" key={`out-${e.id}`}><span className="al-plate">{e.plate}</span><span className="mp-list-time">{e.in_ms != null ? `${clockHM(e.in_ms)} - ${clockHM(e.tsMs)}` : clockHM(e.tsMs)}</span></div>))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
